import React, { useState, ChangeEvent, FormEvent, useCallback, useEffect } from 'react';
import { FiPlus } from 'react-icons/fi';
import { StyledBlog } from '../Blog/Detail/BlogDetail.style';
import { Spacing } from '@pages/CommonStyle/common.style';
import { StyledGridContainer } from '@components/Grid/Grid.style';
import { Title, StyledBlogEditorRowDisplayBlock, PageImageTextarea, StyledDivider, ButtonContainer, BlogEditorButton } from './BlogEditor.style';
import { FormDataType } from './interfaces/index';
import { ContentGroup } from './components/ContentGroup';
import { BlogEditorExportFormat } from './interfaces/index';
import { FormField } from './components/FormField';
import { useContentGroups } from './hooks/useContentGroup';
import { createGitGist, createStandaloneNotionPage } from './services/index';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import { LabelH3 } from '@components/Text/Label/Label.style';
import { Trans } from 'react-i18next';

const BlogEditorPage: React.FC = () => {
  const [formData, setFormData] = useState<Omit<FormDataType, 'contentGroup'>>({
    title: '',
    pageDate: '',
    pageDescription: '',
    pageKeywords: '',
    pageUrl: '',
    pageImg: '',
    pageHashTag1: '',
    pageHashTag2: '',
    pageHashTag3: '',
    pageHashTag4: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  const { addNewContentGroup, removeContentGroup, groups, updateContentGroup } = useContentGroups();

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();

    if (isSaving) return;
    setIsSaving(true);
    try {
      const exportFormat: BlogEditorExportFormat = {
        desc: formData.pageDescription,
        keywords: formData.pageKeywords,
        ogSite: formData.title,
        title: formData.title,
        ogUrl: formData.pageUrl,
        ogImg: formData.pageImg,
        ogType: 'website',
        date: formData.pageDate,
        tag1: formData.pageHashTag1,
        tag2: formData.pageHashTag2,
        tag3: formData.pageHashTag3,
        tag4: formData.pageHashTag4,
        text: groups.map((group) => ({
          head: group.head,
          subHead: group.subHead,
          desc: group.description,
          dot: '',
        })),
      };

      const result = await createGitGist(exportFormat);
      // const result = await createStandaloneNotionPage(exportFormat);

      if (result.success) {
        toast.success('Page created successfully in GIST', {
          position: 'top-center',
          autoClose: 3000,
        });
      } else {
        toast.error(`Error: ${result.message}`, {
          position: 'top-center',
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error(`Error: ${error instanceof Error ? error.message : String(error)}`, {
        position: 'top-center',
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <StyledBlog>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{
          width: '350px',
          fontSize: '16px',
          fontFamily: 'Pretendard',
          textAlign: 'center',
        }}
      />
      <Spacing pixel="128" />
      {/*Form submit */}
      <form onSubmit={handleSave}>
        <StyledGridContainer>
          <Link to={'https://gist.github.com/orbro-daniel'}>
            <LabelH3 size={'large'}>
              <Trans i18nKey={'GIST'} />
            </LabelH3>
          </Link>
          <Spacing pixel="64" />

          <FormField label="Title (h1)" name="title" value={formData.title} onChange={handleChange} />

          <StyledDivider>======== SEO ========</StyledDivider>

          <FormField label="Page Date" name="pageDate" value={formData.pageDate} onChange={handleChange} />

          <FormField label="Page Description" name="pageDescription" value={formData.pageDescription} onChange={handleChange} />

          <FormField label="Page Keywords" name="pageKeywords" value={formData.pageKeywords} onChange={handleChange} />

          <FormField label="Page URL" name="pageUrl" value={formData.pageUrl} onChange={handleChange} />

          <StyledBlogEditorRowDisplayBlock>
            <Title htmlFor="pageImg">Page Img</Title>
            <PageImageTextarea id="pageImg" name="pageImg" value={formData.pageImg} hasValue={formData.pageImg.length > 0} onChange={handleChange} rows={2} />
          </StyledBlogEditorRowDisplayBlock>

          {/* HashTags */}
          {(['pageHashTag1', 'pageHashTag2', 'pageHashTag3', 'pageHashTag4'] as const).map((key, index) => (
            <FormField key={index + 1} label={`Page HashTag${index + 1}`} name={key} value={formData[key]} onChange={handleChange} />
          ))}

          <StyledDivider>======== Content ========</StyledDivider>

          {groups.map((group) => (
            <ContentGroup
              key={group.id}
              group={group}
              onChange={(id, field, value) => {
                updateContentGroup(id, field, value);
              }}
              onRemove={removeContentGroup}
            />
          ))}
          <ButtonContainer>
            <BlogEditorButton fullWidth variant="add" onClick={addNewContentGroup} type="button">
              {FiPlus({ size: 28, color: '#000000', fontWeight: 0 })}
            </BlogEditorButton>
          </ButtonContainer>

          <ButtonContainer>
            <BlogEditorButton fullWidth variant="save" onClick={handleSave} type="submit">
              {isSaving ? 'Saving...' : 'Save'}
            </BlogEditorButton>
          </ButtonContainer>
        </StyledGridContainer>
      </form>
      <Spacing pixel="600" />
    </StyledBlog>
  );
};

export default BlogEditorPage;
